import { config } from './environment'

export class FeatureFlags {
  private static flags: Map<string, boolean> = new Map()
  private static initialized = false
  
  static initialize(overrides?: Record<string, boolean>): void {
    if (this.initialized) return
    
    // Default flags from environment
    Object.entries(config.features).forEach(([key, value]) => {
      this.flags.set(key, value)
    })
    
    // Apply overrides (from API or remote config)
    if (overrides) {
      Object.entries(overrides).forEach(([key, value]) => {
        this.flags.set(key, value)
      })
    }
    
    // Load from localStorage for development
    if (config.environment === 'development') {
      const stored = localStorage.getItem('featureFlags')
      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          Object.entries(parsed).forEach(([key, value]) => {
            this.flags.set(key, value as boolean)
          })
        } catch (error) {
          console.warn('Failed to parse stored feature flags:', error)
        }
      }
    }
    
    this.initialized = true
    
    // Log current flags in development
    if (config.debugMode) {
      console.log('Feature flags initialized:', Object.fromEntries(this.flags))
    }
  }
  
  static isEnabled(feature: string): boolean {
    if (!this.initialized) {
      this.initialize()
    }
    return this.flags.get(feature) ?? false
  }
  
  static enable(feature: string): void {
    this.flags.set(feature, true)
    if (config.environment === 'development') {
      this.saveToLocalStorage()
    }
  }
  
  static disable(feature: string): void {
    this.flags.set(feature, false)
    if (config.environment === 'development') {
      this.saveToLocalStorage()
    }
  }
  
  static toggle(feature: string): void {
    const current = this.isEnabled(feature)
    this.flags.set(feature, !current)
    if (config.environment === 'development') {
      this.saveToLocalStorage()
    }
  }
  
  private static saveToLocalStorage(): void {
    try {
      const obj = Object.fromEntries(this.flags)
      localStorage.setItem('featureFlags', JSON.stringify(obj))
    } catch (error) {
      console.warn('Failed to save feature flags to localStorage:', error)
    }
  }
  
  static getAll(): Record<string, boolean> {
    if (!this.initialized) {
      this.initialize()
    }
    return Object.fromEntries(this.flags)
  }
  
  static reset(): void {
    this.flags.clear()
    if (config.environment === 'development') {
      localStorage.removeItem('featureFlags')
    }
    this.initialize()
  }
  
  // Utility methods for common feature checks
  static canUseBilling(): boolean {
    return this.isEnabled('enableBilling')
  }
  
  static canUseTeamFeatures(): boolean {
    return this.isEnabled('enableTeamFeatures')
  }
  
  static canUseAdvancedAnalytics(): boolean {
    return this.isEnabled('enableAdvancedAnalytics')
  }
  
  static isInMaintenanceMode(): boolean {
    return this.isEnabled('maintenanceMode')
  }
}

// Initialize on module load
FeatureFlags.initialize()